import { TBoardViewMode, TCustomView, TDate, TLoadOrderData } from '@monorepo/types';
import { TDrawModel } from './draw.model';

export type TTimeline = {
  type: 'HOLIDAY' | 'WORKING_DAY';
  isDisable?: boolean;
  date: TDate;
  day: number;
  month: string;
  dateName: string;
  dayLetter: string;
  isPivotDate: boolean;
  isPivotMonth: boolean;
  isMonthEnd: boolean;
};

export interface TRange {
  startDate: TDate;
  endDate: TDate;
}

type TRectDropElementBase = {
  color: string;
  mask: boolean;
  text: {
    value: string | number;
    color: string;
    borderColor: string;
  };
  stripWidth?: '120' | '160';
};

type TSplitStripDropElementData = {
  stripId: string;
  oplId: string;
  factoryId: string;
  qty: number;
} & ({ qtySplitType: 'ABSOLUTE' | 'DELAY' | 'PERCENT' } | { qtySplitType: 'DATE'; splitDate: TDate });

export type TRectDropElement<T3 = unknown> =
  | (TRectDropElementBase & {
      data: T3;
      actionType: 'SHIFT_ORDER';
    })
  | (TRectDropElementBase & {
      data: TLoadOrderData;
      actionType: 'LOAD_ORDER';
    })
  | (TRectDropElementBase & {
      data: TSplitStripDropElementData;
      actionType: 'SPLIT_STRIP';
    })
  | (TRectDropElementBase & {
      data: T3;
      actionType: 'LINK_STRIP';
    })
  | (TRectDropElementBase & {
      data: T3;
      actionType: 'MERGE_STRIPS';
    })
  | (TRectDropElementBase & {
      data: T3;
      actionType: 'UNLOAD_MULTIPLE_ORDERS';
    });

export type TTimelineIndicators = {
  indicators: TIndicators[];
  indicatorMarkers?: TMarkerIndicators;
};

export type TIndicators = {
  date: TDate;
  color: string;
  position: 'START_OF_DAY' | 'END_OF_DAY';
};

export type TMarkerIndicators = {
  left?: {
    color: string;
    isVisible: boolean;
  };
  right?: {
    color: string;
    isVisible: boolean;
  };
};

type TCommonElementProp<T3 = unknown> = {
  id: string;
  name: string;
  range: TRange;
  color: string;
  stackCount: 0 | 1;
  mask: boolean;
  textBorderColor?: string;
  isTextBorderDashed?: boolean;
  /**
   * Scrolls strip into view, changes background color to #FFEB3B and adds a dashed border with color #000
   */
  isStripHighlighted?: boolean;
  borderColor?: string;
  data?: T3;
  isHalfDay?: boolean;
  heightInPercent?: number;
};

type TMarker<T3 = unknown> = {
  type: 'MARKER';
} & TCommonElementProp<T3>;

export type TIconConfig = {
  iconKey:
    | 'LC_APPLIED'
    | 'LOAD_BEFORE_PCD'
    | 'NOTE'
    | 'CLOSE_OPL'
    | 'FABRIC_INHOUSE'
    | 'PRODUCTION_IN_PROGRESS'
    | 'PRODUCTION_COMPLETED';
  svgPath: string;
  svgColor?: string;
  background?: string;
  clickAction?: Extract<TEntityActions, { type: 'STRIP' }>['action'];
  fillRule?: 'nonzero' | 'evenodd';
  text?: string;
};

export type TStripElement<T3 = unknown> = {
  type: 'STRIP';
  textList?: {
    value: string;
    color: string;
    align?: 'top' | 'center' | 'bottom';
    justify?: 'left' | 'center' | 'right';
    fontSizeInPx?: number;
  }[];
  note?: string;
  iconList: TIconConfig[];
  additionalInfo?: string;
  isStripOutdated?: boolean;
  linkedToPrevious?: boolean;
  progressFill?: {
    fillInPercent: number;
    pattern: keyof TDrawModel['defsIds'];
  };
} & TCommonElementProp<T3>;

type TMesh<T3 = unknown> = {
  type: 'MESH';
} & TCommonElementProp<T3>;

type TCircle<T3 = unknown> = {
  type: 'CIRCLE';
} & TCommonElementProp<T3>;

export type TRectElements<T3 = unknown> = TStripElement<T3> | TMesh<T3> | TMarker<T3>;
export type TCircleElements<T3 = unknown> = TCircle<T3>;
type TElement<T3> = TRectElements<T3> | TCircleElements<T3>;

// export type TElementGroup = {
//   [k in TElement['type']]: Extract<TElement, { type: k }>[];
// };
// Generic type parameters:
// T1: Type for entity group data
// T2: Type for individual entity data
// T3: Type for element data
export type TEntityGroups<T1 = unknown, T2 = unknown, T3 = unknown> = {
  id: string;
  name: string;
  data: T1; // Entity group data of type T1
  holidays: TDate[];
  entities: TTimelineRow<T2, T3>[]; // Array of TTimelineRow with T2 and T3 types
  backgroundColor?: string;
};

type TLeftNavIcon = {
  name: 'EXCEEDING_WORKFORCE';
  tooltip: string;
  svgPath: string;
  fill: string;
  isClickable: boolean;
};

type TTimelineRow<T2 = unknown, T3 = unknown> = {
  id: string;
  data: T2; // Individual entity data of type T2
  name: {
    displayTxt: string;
    isClickable?: boolean;
  };
  elements: TElement<T3>[]; // Array of TElement with T3 type
  metadata?: {
    title: string;
    subTitle: string;
    leftCount: number;
    rightCount: number;
    icon?: TLeftNavIcon;
  };
  disabled?: boolean;
};

export type TBoardView = {
  boardViewType: TBoardViewMode;
  themeView?: {
    key: TCustomView['viewMode'];
    displayName: string;
  };
  offset?: 'PREV' | 'NEXT' | 'DEFAULT';
  currentDate?: string;
  dateRange?: {
    from: string;
    to: string;
  };
};

export type TTimelineModel<T1 = unknown, T2 = unknown, T3 = unknown> = {
  boardView: TBoardView;
  pivot: TDate;
  timeline: TTimeline[];
  weekends: TDate[];
  endOfMonths: TDate[];
  months: {
    start: TDate;
    end: TDate;
  }[];
  entities: TEntityGroups<T1, T2, T3>[]; // Array of TEntityGroups with T1, T2, and T3 types
  comptedEntities: {
    totalEntities: number;
    totalGroupEntities: number;
  };
  window?: [start: TDate, end: TDate];
  disable?: {
    next: boolean;
    prev: boolean;
  };
  hideLeftPanel?: boolean;
};

export type TEntityActions<T1 = unknown, T2 = unknown, T3 = unknown> =
  | {
      type: 'STRIP';
      action: 'LEFT_CLICK' | 'RIGHT_CLICK' | 'NOTE_CLICK';
      event: Event;
      elementData: T3;
      entityData: T2;
      entityGroupData: T1;
    }
  | {
      type: 'LEFT_NAV';
      action: 'TITLE_CLICK';
      event: Event;
      entityData: T2;
    }
  | {
      type: 'LEFT_NAV';
      action: 'ICON_CLICK';
      event: Event;
      entityData: T2;
      iconName: TLeftNavIcon['name'];
      entityGroupData: T1;
    }
  | {
      type: 'STRIP_TRACK';
      action: 'CLICK';
      event: Event;
      entityData: T2;
      entityGroupData?: T1;
      date: TDate;
      stripsTrackedOnMouse: TRectDropElement<T3>[];
    }
  | {
      type: 'STRIP_ADD';
      action: 'SHIFT+CLICK';
      event: Event;
      entityData: unknown;
      entityGroupData?: unknown;
      date: TDate;
      stripsTrackedOnMouse: TRectDropElement<T3>[];
      stripAtLocationElementData: T3;
    };

export type TEntityActionCb<T1 = unknown, T2 = unknown, T3 = unknown> = (
  stripEvent: TEntityActions<T1, T2, T3>,
) => void;

export type TToolbarAction =
  | 'SHIFT_FILTER'
  | 'FACTORY_FILTER'
  | 'LOAD_ORDER'
  | 'SYNC'
  | 'RECALIBERATE'
  | 'TASK_MANAGER'
  | 'LINE_PLAN'
  | 'PRINT'
  | 'MORE'
  | 'SYNC_IN_PROGRESS'
  | 'FILTERS'
  | 'SEARCH'
  | 'MATERIAL_IN_HOUSE_UPLOAD';

export type TTimelineConfiguration = {
  renderType: 'TNA_BOARD' | 'LL_BOARD' | 'HL_BOARD' | 'CAPACITY_BOARD' | 'PLANNING_BOARD' | 'ORDER_TIMELINE_VIEW_BOARD';
  hideCalendarHolidays?: boolean;
  enableDateClick?: boolean;
  enableZoom?: boolean;
  enableHorizontalScroll?: boolean;
};

export const TIMELINE_MIN_DAYS_TO_SHOW = 3;
